import React, { useState, useEffect } from 'react';
import { CardInput, CardBrand, DEMO_TEST_CARDS } from '../../types';

interface CardFormProps {
    onSubmit: (cardData: CardInput) => void;
    isProcessing: boolean;
    submitLabel?: string;
}

const CardForm: React.FC<CardFormProps> = ({ onSubmit, isProcessing, submitLabel = 'Ödeme Yap' }) => {
    const [cardNumber, setCardNumber] = useState('');
    const [cardName, setCardName] = useState('');
    const [expiry, setExpiry] = useState('');
    const [cvc, setCvc] = useState('');
    const [brand, setBrand] = useState<CardBrand>('unknown');
    const [errors, setErrors] = useState<Record<string, string>>({});

    // Detect card brand
    useEffect(() => {
        const num = cardNumber.replace(/\D/g, '');
        if (num.startsWith('4')) setBrand('visa');
        else if (num.startsWith('5')) setBrand('mastercard');
        else if (num.startsWith('9')) setBrand('troy'); // Simplified Troy check
        else if (num.startsWith('3')) setBrand('amex');
        else setBrand('unknown');
    }, [cardNumber]);

    const formatCardNumber = (val: string) => {
        const v = val.replace(/\D/g, '').substring(0, 16);
        const parts = [];
        for (let i = 0; i < v.length; i += 4) {
            parts.push(v.substring(i, i + 4));
        }
        return parts.join(' ');
    };

    const formatExpiry = (val: string) => {
        const v = val.replace(/\D/g, '').substring(0, 4);
        if (v.length >= 2) {
            return `${v.substring(0, 2)}/${v.substring(2)}`;
        }
        return v;
    };

    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};
        const num = cardNumber.replace(/\D/g, '');

        // Luhn Algorithm
        let sum = 0;
        let shouldDouble = false;
        for (let i = num.length - 1; i >= 0; i--) {
            let digit = parseInt(num.charAt(i));
            if (shouldDouble) {
                digit *= 2;
                if (digit > 9) digit -= 9;
            }
            sum += digit;
            shouldDouble = !shouldDouble;
        }

        // Allow Demo Test Cards to bypass Luhn
        const isDemoCard = (Object.values(DEMO_TEST_CARDS) as string[]).includes(num);
        const isValidLuhn = (sum % 10 === 0 && num.length === 16) || isDemoCard;

        if (!isValidLuhn) newErrors.cardNumber = 'Geçersiz kart numarası';
        if (cardName.length < 5) newErrors.cardName = 'Kart üzerindeki isim gereklidir';
        if (expiry.length !== 5) newErrors.expiry = 'Geçersiz tarih (AA/YY)';
        if (cvc.length < 3) newErrors.cvc = 'Geçersiz CVC';

        // Expiry check (simple)
        if (expiry.length === 5) {
            const [mm, yy] = expiry.split('/').map(Number);
            const now = new Date();
            const currentYear = parseInt(now.getFullYear().toString().substring(2));
            const currentMonth = now.getMonth() + 1;

            if (mm < 1 || mm > 12) newErrors.expiry = 'Geçersiz ay';
            else if (yy < currentYear || (yy === currentYear && mm < currentMonth)) {
                newErrors.expiry = 'Kartın süresi dolmuş';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (validate()) {
            const [expMonth, expYear] = expiry.split('/');
            onSubmit({
                cardNumber: cardNumber.replace(/\D/g, ''),
                cardHolderName: cardName,
                expireMonth: expMonth,
                expireYear: `20${expYear}`,
                cvc
            });
        }
    };

    return (
        <div className="bg-white dark:bg-gray-900 p-6 rounded-2xl border border-gray-100 dark:border-white/5 shadow-sm transition-colors duration-300">
            {/* Visual Card Representation */}
            <div className={`mb-8 p-6 rounded-2xl text-white shadow-lg transition-all duration-500 relative overflow-hidden h-48 flex flex-col justify-between ${brand === 'visa' ? 'bg-gradient-to-r from-blue-600 to-blue-800' :
                brand === 'mastercard' ? 'bg-gradient-to-r from-orange-500 to-red-600' :
                    'bg-gradient-to-r from-gray-700 to-gray-900'
                }`}>
                {/* Background Patterns */}
                <div className="absolute top-0 right-0 -mt-10 -mr-10 w-40 h-40 bg-white opacity-10 rounded-full blur-xl"></div>
                <div className="absolute bottom-0 left-0 -mb-10 -ml-10 w-40 h-40 bg-white opacity-10 rounded-full blur-xl"></div>

                <div className="flex justify-between items-start">
                    <div className="w-12 h-8 bg-yellow-400/80 rounded-md backdrop-blur-sm"></div> {/* Chip */}
                    <div className="text-xl font-bold italic tracking-wider uppercase opacity-90">{brand !== 'unknown' ? brand : 'BANK'}</div>
                </div>

                <div className="space-y-6 relative z-10">
                    <div className="text-2xl font-mono tracking-widest drop-shadow-md">
                        {cardNumber || '•••• •••• •••• ••••'}
                    </div>
                    <div className="flex justify-between items-end">
                        <div>
                            <div className="text-[10px] uppercase opacity-70 mb-1">Kart Sahibi</div>
                            <div className="font-medium tracking-wide truncate max-w-[200px] uppercase">
                                {cardName || 'AD SOYAD'}
                            </div>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase opacity-70 mb-1">SKT</div>
                            <div className="font-mono">{expiry || 'MM/YY'}</div>
                        </div>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-1">
                    <label className="text-sm font-bold text-gray-700 dark:text-gray-400 ml-1">Kart Üzerindeki İsim</label>
                    <input
                        type="text"
                        className={`w-full h-12 px-4 rounded-xl border bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:bg-white dark:focus:bg-gray-700 transition-all outline-none ${errors.cardName ? 'border-red-300 dark:border-red-500/50 focus:border-red-500' : 'border-gray-200 dark:border-white/10 focus:border-brand-terracotta focus:ring-2 focus:ring-brand-terracotta/10'}`}
                        placeholder="AD SOYAD"
                        value={cardName}
                        onChange={(e) => {
                            setCardName(e.target.value.toUpperCase());
                            if (errors.cardName) setErrors({ ...errors, cardName: '' });
                        }}
                        maxLength={50}
                    />
                    {errors.cardName && <p className="text-red-500 text-xs ml-1 font-medium animate-in slide-in-from-top-1">{errors.cardName}</p>}
                </div>

                <div className="space-y-1">
                    <label className="text-sm font-bold text-gray-700 dark:text-gray-400 ml-1">Kart Numarası</label>
                    <div className="relative">
                        <input
                            type="text"
                            className={`w-full h-12 px-4 rounded-xl border bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:bg-white dark:focus:bg-gray-700 transition-all outline-none font-mono ${errors.cardNumber ? 'border-red-300 dark:border-red-500/50 focus:border-red-500' : 'border-gray-200 dark:border-white/10 focus:border-brand-terracotta focus:ring-2 focus:ring-brand-terracotta/10'}`}
                            placeholder="0000 0000 0000 0000"
                            value={cardNumber}
                            onChange={(e) => {
                                setCardNumber(formatCardNumber(e.target.value));
                                if (errors.cardNumber) setErrors({ ...errors, cardNumber: '' });
                            }}
                            maxLength={19}
                        />
                        <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                            {brand === 'visa' && <span className="text-blue-600 font-bold italic">VISA</span>}
                            {brand === 'mastercard' && <div className="flex -space-x-2"><div className="w-6 h-6 rounded-full bg-red-500 opacity-80"></div><div className="w-6 h-6 rounded-full bg-orange-500 opacity-80"></div></div>}
                        </div>
                    </div>
                    {errors.cardNumber && <p className="text-red-500 text-xs ml-1 font-medium animate-in slide-in-from-top-1">{errors.cardNumber}</p>}
                </div>

                <div className="flex gap-4">
                    <div className="w-1/2 space-y-1">
                        <label className="text-sm font-bold text-gray-700 dark:text-gray-400 ml-1">Son Kul. Tarihi</label>
                        <input
                            type="text"
                            className={`w-full h-12 px-4 rounded-xl border bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:bg-white dark:focus:bg-gray-700 transition-all outline-none text-center font-mono ${errors.expiry ? 'border-red-300 dark:border-red-500/50 focus:border-red-500' : 'border-gray-200 dark:border-white/10 focus:border-brand-terracotta focus:ring-2 focus:ring-brand-terracotta/10'}`}
                            placeholder="MM/YY"
                            value={expiry}
                            onChange={(e) => {
                                setExpiry(formatExpiry(e.target.value));
                                if (errors.expiry) setErrors({ ...errors, expiry: '' });
                            }}
                            maxLength={5}
                        />
                        {errors.expiry && <p className="text-red-500 text-xs ml-1 font-medium animate-in slide-in-from-top-1">{errors.expiry}</p>}
                    </div>

                    <div className="w-1/2 space-y-1">
                        <label className="text-sm font-bold text-gray-700 dark:text-gray-400 ml-1 flex items-center justify-between">
                            CVC / CVV
                            <span className="text-gray-400 dark:text-gray-500 text-[10px] font-normal cursor-help" title="Kartın arkasındaki 3 haneli kod">?</span>
                        </label>
                        <input
                            type="text"
                            className={`w-full h-12 px-4 rounded-xl border bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:bg-white dark:focus:bg-gray-700 transition-all outline-none text-center font-mono ${errors.cvc ? 'border-red-300 dark:border-red-500/50 focus:border-red-500' : 'border-gray-200 dark:border-white/10 focus:border-brand-terracotta focus:ring-2 focus:ring-brand-terracotta/10'}`}
                            placeholder="•••"
                            value={cvc}
                            onChange={(e) => {
                                setCvc(e.target.value.replace(/\D/g, '').substring(0, 4));
                                if (errors.cvc) setErrors({ ...errors, cvc: '' });
                            }}
                            maxLength={4}
                        />
                        {errors.cvc && <p className="text-red-500 text-xs ml-1 font-medium animate-in slide-in-from-top-1">{errors.cvc}</p>}
                    </div>
                </div>

                <button
                    type="submit"
                    disabled={isProcessing}
                    className="w-full h-14 bg-black dark:bg-white text-white dark:text-gray-900 rounded-xl font-bold text-lg hover:shadow-xl hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                    {isProcessing ? (
                        <>
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            İşleniyor...
                        </>
                    ) : (
                        <>
                            <span>🔒</span> {submitLabel}
                        </>
                    )}
                </button>
                <p className="text-center text-[10px] text-gray-400 dark:text-gray-600 flex items-center justify-center gap-1">
                    <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
                    256-bit SSL ile güvenli ödeme. Kart bilgileriniz saklanmaz.
                </p>
            </form>
        </div>
    );
};

export default CardForm;
